import { useRef, useEffect } from 'react';

export default function CodeRain() {
  const canvasRef = useRef(null);
  const mouseRef = useRef({ x: -1000, y: -1000 });

  useEffect(() => {
    const onMouseMove = (e) => {
      mouseRef.current = { x: e.clientX, y: e.clientY };
    };
    window.addEventListener('mousemove', onMouseMove);
    return () => window.removeEventListener('mousemove', onMouseMove);
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');

    const resize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    resize();

    const chars = '01{}[]<>;:=+-_~|^!@#$%&*()ABCDEFabcdef';
    const fontSize = 13;
    let columns = Math.floor(canvas.width / fontSize);
    let drops = Array(columns).fill(0).map(() => Math.random() * -50);

    const draw = () => {
      ctx.fillStyle = 'rgba(5, 5, 5, 0.07)';
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      const mx = mouseRef.current.x;
      const my = mouseRef.current.y;
      const radius = 140;

      ctx.font = `${fontSize}px "JetBrains Mono", monospace`;

      for (let i = 0; i < drops.length; i++) {
        const char = chars[Math.floor(Math.random() * chars.length)];
        const x = i * fontSize;
        const y = drops[i] * fontSize;

        const dx = x - mx;
        const dy = y - my;
        const dist = Math.sqrt(dx * dx + dy * dy);

        if (dist < radius) {
          const intensity = 1 - dist / radius;
          const angle = Math.atan2(dy, dx);
          const push = intensity * 18;
          const px = x + Math.cos(angle) * push;
          const py = y + Math.sin(angle) * push;

          ctx.fillStyle = `rgba(255, 255, 255, ${0.04 + intensity * 0.35})`;
          const scale = 1 + intensity * 0.5;
          ctx.save();
          ctx.translate(px, py);
          ctx.scale(scale, scale);
          ctx.fillText(char, 0, 0);
          ctx.restore();
        } else {
          ctx.fillStyle = 'rgba(255, 255, 255, 0.035)';
          ctx.fillText(char, x, y);
        }

        if (drops[i] * fontSize > canvas.height && Math.random() > 0.975) {
          drops[i] = 0;
        }
        drops[i] += 0.4;
      }
    };

    let raf = 0;
    let last = 0;
    const FRAME_MS = 55; // ~18fps is plenty for ambient background rain
    const loop = (t) => {
      raf = requestAnimationFrame(loop);
      if (document.hidden || t - last < FRAME_MS) return;
      last = t;
      draw();
    };
    const prefersReduced = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReduced) {
      draw(); // render a single static frame, then stop
    } else {
      raf = requestAnimationFrame(loop);
    }

    const onResize = () => {
      resize();
      columns = Math.floor(canvas.width / fontSize);
      drops = Array(columns).fill(0).map(() => Math.random() * -50);
    };
    window.addEventListener('resize', onResize);

    return () => {
      if (raf) cancelAnimationFrame(raf);
      window.removeEventListener('resize', onResize);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      data-testid="code-rain"
      className="fixed inset-0 z-0 pointer-events-none"
    />
  );
}
